import { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiError, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Sparkles, Search, Plus, CreditCard, Package, Plane, TrendingUp, Home as HomeIcon, Star } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Link } from "react-router-dom";

const CAT_META = {
    Purchase: { icon: Package, tone: "stone" },
    Travel: { icon: Plane, tone: "sky" },
    Home: { icon: HomeIcon, tone: "emerald" },
    Family: { icon: Star, tone: "amber" },
    Career: { icon: Sparkles, tone: "violet" },
    Health: { icon: Sparkles, tone: "rose" },
    Vehicle: { icon: CreditCard, tone: "stone" },
    Financial: { icon: TrendingUp, tone: "teal" },
    Milestone: { icon: Star, tone: "amber" },
    Other: { icon: Sparkles, tone: "stone" },
};

const TONE_CLASSES = {
    stone: "text-stone-700 bg-stone-100 border-stone-200",
    sky: "text-sky-700 bg-sky-50 border-sky-200",
    emerald: "text-emerald-700 bg-emerald-50 border-emerald-200",
    amber: "text-amber-800 bg-amber-50 border-amber-200",
    violet: "text-violet-700 bg-violet-50 border-violet-200",
    rose: "text-rose-700 bg-rose-50 border-rose-200",
    teal: "text-teal-700 bg-teal-50 border-teal-200",
};

const CATEGORIES = ["Purchase", "Travel", "Home", "Family", "Career", "Health", "Vehicle", "Financial", "Milestone", "Other"];

const fmtMoney = (n, ccy = "INR") =>
    n == null ? "" : `${ccy === "INR" ? "₹" : ccy + " "}${Number(n).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

export default function Timeline() {
    const { hasRole } = useAuth();
    const canEdit = hasRole && hasRole("admin", "finance", "expense_user");
    const [events, setEvents] = useState([]);
    const [byYear, setByYear] = useState({});
    const [q, setQ] = useState("");
    const [filter, setFilter] = useState(new Set());
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const [f, setF] = useState({
        date: new Date().toISOString().slice(0, 10),
        title: "", description: "", category: "Milestone",
        cost: "", currency: "INR", tags: "",
    });

    const load = useCallback(async () => {
        try {
            const params = new URLSearchParams();
            if (q) params.set("q", q);
            if (filter.size > 0) params.set("categories", Array.from(filter).join(","));
            const { data } = await api.get(`/timeline?${params.toString()}`);
            setEvents(data.events || []);
            setByYear(data.by_year || {});
        } catch (err) { toast.error(formatApiError(err)); }
    }, [q, filter]);
    useEffect(() => { load(); }, [load]);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const body = { ...f };
            body.tags = f.tags ? f.tags.split(",").map((s) => s.trim()).filter(Boolean) : null;
            if (body.cost === "" || body.cost == null) delete body.cost; else body.cost = Number(body.cost);
            await api.post("/life-events", body);
            toast.success("Milestone added to your timeline");
            setOpen(false);
            setF({ ...f, title: "", description: "", cost: "", tags: "" });
            load();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBusy(false); }
    };

    // 5 pre-filled onboarding chips — surface only when the timeline has no
    // user-authored milestones yet. Tapping a chip opens the dialog with
    // fields pre-populated so it feels like a story-in-progress.
    const STARTER_CHIPS = [
        { title: "Bought first Mac", category: "Purchase", tags: "apple, computer" },
        { title: "Started home construction", category: "Home", tags: "home, milestone" },
        { title: "First international trip", category: "Travel", tags: "travel, milestone" },
        { title: "First job", category: "Career", tags: "career, milestone" },
        { title: "Bought first car", category: "Vehicle", tags: "vehicle, purchase" },
    ];
    const hasUserAuthored = events.some((e) => e.source === "life_event");
    const pickChip = (chip) => {
        setF({
            date: f.date, title: chip.title, description: "",
            category: chip.category, cost: "", currency: "INR", tags: chip.tags,
        });
        setOpen(true);
    };

    const toggleFilter = (cat) => {
        setFilter((old) => {
            const next = new Set(old);
            if (next.has(cat)) next.delete(cat); else next.add(cat);
            return next;
        });
    };

    // Group events by year for headers
    const grouped = useMemo(() => {
        const g = new Map();
        events.forEach((e) => {
            const y = (e.date || "").slice(0, 4);
            if (!g.has(y)) g.set(y, []);
            g.get(y).push(e);
        });
        return Array.from(g.entries()); // [[year, [events...]], ...]
    }, [events]);

    const yearsAvailable = Object.keys(byYear).sort().reverse();

    const linkFor = (ev) => {
        if (ev.link?.asset_id) return `/assets/${ev.link.asset_id}`;
        if (ev.link?.card_id && ev.source === "card_added") return `/cards/${ev.link.card_id}`;
        return null;
    };

    return (
        <div data-testid="timeline-page">
            <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
                <div>
                    <div className="eyebrow flex items-center gap-1.5"><Sparkles size={12} /> Life Timeline</div>
                    <h1 className="font-display text-4xl sm:text-5xl text-stone-900 leading-none mt-2">
                        Your life, in chronological memory.
                    </h1>
                    <p className="text-sm text-stone-500 mt-3 max-w-2xl">
                        Every card, every asset, every trip, every high-value purchase and every milestone
                        you record &mdash; woven into one searchable, filterable, timeline. Try:
                        <span className="italic"> &ldquo;germany&rdquo;, &ldquo;home&rdquo;, &ldquo;macbook&rdquo;.</span>
                    </p>
                </div>
                {canEdit && (
                    <Dialog open={open} onOpenChange={setOpen}>
                        <DialogTrigger asChild>
                            <Button className="bg-stone-900 hover:bg-stone-800 text-white" data-testid="timeline-add">
                                <Plus size={14} className="mr-1.5" /> Add milestone
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-lg" data-testid="timeline-add-dialog">
                            <DialogHeader>
                                <DialogTitle className="font-display text-2xl">New milestone</DialogTitle>
                                <DialogDescription className="text-[13px] text-stone-500">
                                    Any moment worth remembering &mdash; not just financial ones.
                                </DialogDescription>
                            </DialogHeader>
                            <form onSubmit={submit} className="space-y-3">
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <Label className="eyebrow">Date</Label>
                                        <Input type="date" required value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} className="mt-1.5" data-testid="tl-date" />
                                    </div>
                                    <div>
                                        <Label className="eyebrow">Category</Label>
                                        <Select value={f.category} onValueChange={(v) => setF({ ...f, category: v })}>
                                            <SelectTrigger className="mt-1.5" data-testid="tl-category"><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="col-span-2">
                                        <Label className="eyebrow">Title *</Label>
                                        <Input required value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className="mt-1.5" placeholder="e.g. Germany trip started" data-testid="tl-title" />
                                    </div>
                                    <div className="col-span-2">
                                        <Label className="eyebrow">Description</Label>
                                        <Textarea rows={3} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className="mt-1.5" />
                                    </div>
                                    <div>
                                        <Label className="eyebrow">Cost (optional)</Label>
                                        <Input type="number" step="0.01" value={f.cost} onChange={(e) => setF({ ...f, cost: e.target.value })} className="mt-1.5 font-mono-figure" />
                                    </div>
                                    <div>
                                        <Label className="eyebrow">Tags (comma-separated)</Label>
                                        <Input value={f.tags} onChange={(e) => setF({ ...f, tags: e.target.value })} className="mt-1.5" placeholder="germany, work, munich" />
                                    </div>
                                </div>
                                <Button type="submit" disabled={busy} className="w-full bg-stone-900 hover:bg-stone-800 text-white" data-testid="tl-submit">
                                    {busy ? "Saving…" : "Add to timeline"}
                                </Button>
                            </form>
                        </DialogContent>
                    </Dialog>
                )}
            </div>

            {canEdit && !hasUserAuthored && events.length > 0 && (
                <div className="rounded-lg border border-emerald-200 bg-emerald-50/40 p-5 mb-6" data-testid="tl-onboarding">
                    <div className="flex items-start gap-2 mb-3">
                        <Sparkles size={14} className="text-emerald-700 mt-0.5" />
                        <div>
                            <div className="text-sm font-medium text-stone-900">Add your first milestone in 30 seconds</div>
                            <div className="text-[12px] text-stone-500 mt-0.5">
                                Tap any chip below to start &mdash; you can edit before saving.
                            </div>
                        </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {STARTER_CHIPS.map((c) => (
                            <button
                                key={c.title}
                                type="button"
                                className="text-[12px] px-3 py-1.5 rounded-full bg-white border border-emerald-200 text-emerald-800 hover:border-emerald-400 transition-colors"
                                onClick={() => pickChip(c)}
                                data-testid={`tl-chip-${c.title.replace(/\s/g, "-")}`}
                            >
                                + {c.title}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* Filter chips + search */}
            <div className="flex flex-wrap items-center gap-2 mb-6">
                <div className="relative flex-1 min-w-[240px] max-w-md">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
                    <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search everything (e.g. Germany, MacBook, home)" className="pl-9" data-testid="tl-search" />
                </div>
                <div className="flex flex-wrap gap-1.5">
                    {CATEGORIES.map((c) => {
                        const active = filter.has(c);
                        const meta = CAT_META[c] || CAT_META.Other;
                        return (
                            <button
                                key={c}
                                onClick={() => toggleFilter(c)}
                                className={`text-[11px] uppercase tracking-widest px-2.5 py-1 rounded-full border transition-all ${
                                    active ? TONE_CLASSES[meta.tone] : "bg-white text-stone-500 border-stone-200 hover:border-stone-300"
                                }`}
                                data-testid={`tl-chip-${c}`}
                            >
                                {c}
                            </button>
                        );
                    })}
                </div>
                <div className="text-[11px] text-stone-500 ml-auto">
                    {events.length} {events.length === 1 ? "event" : "events"}
                    {yearsAvailable.length > 0 && <> · {yearsAvailable.length} year{yearsAvailable.length === 1 ? "" : "s"}</>}
                </div>
            </div>

            {events.length === 0 && (
                <div className="rounded-lg border border-dashed border-stone-300 bg-white p-14 text-center" data-testid="tl-empty">
                    <Sparkles size={20} className="mx-auto mb-3 text-stone-400" />
                    <div className="text-sm text-stone-600">No events match your filters yet.</div>
                    <div className="text-[12px] text-stone-500 mt-2">
                        Add a card, an asset, or a milestone to seed your timeline.
                    </div>
                </div>
            )}

            {/* Timeline groups */}
            <div className="relative">
                <div className="absolute left-[10px] top-2 bottom-2 w-px bg-stone-200 hidden sm:block" />
                {grouped.map(([year, list]) => (
                    <div key={year} className="mb-8">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-5 h-5 rounded-full bg-stone-900 text-white text-[9px] font-medium flex items-center justify-center relative z-10" />
                            <h2 className="font-display text-3xl text-stone-900 leading-none">{year}</h2>
                            <div className="text-[11px] uppercase tracking-widest text-stone-500 mt-2">
                                · {list.length} events
                            </div>
                        </div>
                        <div className="space-y-3 sm:pl-8">
                            {list.map((ev) => {
                                const meta = CAT_META[ev.category] || CAT_META.Other;
                                const Icon = meta.icon;
                                const target = linkFor(ev);
                                const body = (
                                    <div className="flex items-start gap-3 rounded-lg border border-stone-200 bg-white p-4 hover:border-stone-300 transition-colors" data-testid={`tl-event-${ev.id}`}>
                                        <div className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center border ${TONE_CLASSES[meta.tone]}`}>
                                            <Icon size={13} />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-start justify-between gap-3 flex-wrap">
                                                <div className="min-w-0">
                                                    <div className="text-sm font-medium text-stone-900 truncate">{ev.title}</div>
                                                    {ev.subtitle && <div className="text-[12px] text-stone-500 mt-0.5 line-clamp-2">{ev.subtitle}</div>}
                                                </div>
                                                <div className="text-right">
                                                    <div className="text-[11px] text-stone-500 tabular-nums">{fmtDate(ev.date)}</div>
                                                    {ev.cost != null && (
                                                        <div className="font-mono-figure text-sm text-stone-900 mt-0.5">{fmtMoney(ev.cost, ev.currency)}</div>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                                                <span className={`text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded border ${TONE_CLASSES[meta.tone]}`}>
                                                    {ev.category}
                                                </span>
                                                {(ev.meta?.tags || []).slice(0, 3).map((t) => (
                                                    <span key={t} className="text-[10px] px-1.5 py-0.5 rounded-full bg-stone-50 text-stone-600 border border-stone-200">#{t}</span>
                                                ))}
                                                {ev.source !== "life_event" && (
                                                    <span className="text-[10px] text-stone-400 ml-auto">from {ev.source.replace(/_/g, " ")}</span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                                return target ? (
                                    <Link key={ev.id} to={target} className="block relative">
                                        <div className="absolute left-[-22px] top-6 w-4 h-4 rounded-full bg-white border-2 border-stone-300 hidden sm:block" />
                                        {body}
                                    </Link>
                                ) : (
                                    <div key={ev.id} className="relative">
                                        <div className="absolute left-[-22px] top-6 w-4 h-4 rounded-full bg-white border-2 border-stone-300 hidden sm:block" />
                                        {body}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
